/**
 * Contract-Specific Actions
 * Actions defined specifically for the contracts entity
 * Strongly typed to contract row type
 * Uses shared builders to eliminate duplication
 */

import type { DetailActionStrict } from "@/lib/actions/types/detail-action";
import {
  universalDeleteAction,
  universalViewDetailsAction,
  universalConfigureAutomatedTasksAction,
} from "@/lib/actions/definitions/universal-actions";
import { ACTION_ICONS, ACTION_VARIANTS, ROLE_COMBINATIONS } from "@/lib/actions/types/action-properties";
import type { EntityRowType } from "@/lib/actions/types/entity-keys";

/** The contract row type the action registry expects for the "contracts" key. */
type ContractRow = EntityRowType<"contracts">;

/**
 * Complete contract actions array
 * Combines universal and contract-specific actions
 */
export const contractDetailActions: DetailActionStrict<ContractRow>[] = [
  // PRIMARY ACTIONS (Header)
  // Edit opens the dedicated EditContractModal (Hub-side linkage + document name
  // + notes), NOT the generic entity edit modal — every contract field is
  // read-only in the entity config since the provider owns the document.
  {
    id: "edit",
    label: "Edit",
    icon: ACTION_ICONS.EDIT,
    variant: ACTION_VARIANTS.DEFAULT,
    action: "editContract",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
    primary: true,
  },
  universalDeleteAction<ContractRow>(),

  // TABLE-ONLY ACTIONS
  universalViewDetailsAction<ContractRow>("viewDetails"),

  // SECONDARY ACTIONS (Menu)
  // E-signature cleanup actions.
  {
    id: "resend",
    label: "Resend signature request",
    icon: ACTION_ICONS.REFRESH,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "resendContract",
    allowedRoles: [...ROLE_COMBINATIONS.EMPLOYEE_AND_ADMIN],
  },
  {
    id: "withdraw",
    label: "Withdraw / Void contract",
    icon: ACTION_ICONS.X_CIRCLE,
    variant: ACTION_VARIANTS.DESTRUCTIVE,
    action: "withdrawContract",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  },
  {
    id: "renew",
    label: "Renew Contract",
    icon: ACTION_ICONS.REFRESH,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "renewContract",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  },
  {
    id: "terminate",
    label: "Terminate Contract",
    icon: ACTION_ICONS.X_CIRCLE,
    variant: ACTION_VARIANTS.DESTRUCTIVE,
    action: "terminateContract",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  },
  {
    id: "duplicate",
    label: "Duplicate Contract",
    icon: ACTION_ICONS.DUPLICATE,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "duplicateContract",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  },
  {
    id: "export",
    label: "Export Contract",
    icon: ACTION_ICONS.DOWNLOAD,
    variant: ACTION_VARIANTS.OUTLINE,
    action: "exportContract",
    allowedRoles: [...ROLE_COMBINATIONS.ADMIN_ONLY],
  },
  universalConfigureAutomatedTasksAction<ContractRow>(),
];
